import { RequestType } from '@/enums'
import {
  BadRequestError,
  ForbiddenError,
  NetworkError,
  ServerError,
  TooManyRequestsError,
  UnauthorisedError
} from '@/errors/index.js'
import { useLocaleStore } from '@/features/locales/stores/locale'
import { useAuthStore } from '@/pinia/auth'
import { useBrandedStore } from '@/pinia/branded'
import { config } from '@/utils/config'
import { captureEvent } from '@sentry/browser'
import DOMPurify from 'dompurify'

let refreshPromise = null

export async function request({ method, requestType = RequestType.POST, payload = {}, signal, retryCount = 0 }) {
  const localeStore = useLocaleStore()
  const authStore = useAuthStore()
  const url = method.endpoint ? method.endpoint + method.method : config.api_url + method
  const isFormData = payload instanceof FormData
  const headers = {
    'Content-Type': 'application/json',
    'Accept-Language': localeStore.locale,
    'X-Quotila-App': window.VITE_APP_INSTALLATION_TYPE
  }
  if (isFormData) {
    delete headers['Content-Type']
  }
  const payloadParsed = isFormData ? payload : JSON.stringify(payload)
  try {
    if (shouldRefresh(authStore, method)) {
      const refresh = await refreshAuthCookie(localeStore.locale)
      if (refresh) {
        authStore.setSessionExpiry(refresh)
      }
    }

    const response = await fetch(url, {
      method: requestType === RequestType.POST ? 'POST' : 'GET',
      credentials: 'include',
      headers,
      signal,
      body: requestType === RequestType.POST ? payloadParsed : undefined
    })

    if (!response.ok) {
      const raw = await response.text()
      const error = new Error(`HTTP ${response.status}`)
      error.payload = payload
      error.response = {
        status: response.status,
        data: raw,
        headers: Object.fromEntries(response.headers.entries())
      }
      throw error
    }
    return parseResponse(response)
  } catch (error) {
    if (error?.name === 'AbortError') return { cancelled: true }
    if (retryCount < 2 && error instanceof TypeError && error.message?.startsWith('Failed to fetch')) {
      return request({
        method,
        requestType,
        payload,
        signal,
        retryCount: retryCount + 1
      })
    }
    error.retryCount = retryCount
    return handleHttpError(error, method?.method || method)
  }
}

function canRefreshForMethod(method) {
  const normalizedMethod = `${method?.method || method || ''}`.toLowerCase()
  return !['auth/refresh', 'auth/getcookie', 'auth/getjwt'].includes(normalizedMethod)
}

function shouldRefresh(authStore, method) {
  if (!authStore.loggedIn || !authStore.sessionRefreshAfter || !canRefreshForMethod(method)) {
    return false
  }

  return new Date(authStore.sessionRefreshAfter).getTime() <= Date.now()
}

function refreshAuthCookie(locale) {
  if (refreshPromise) {
    return refreshPromise
  }

  // No signal: the refresh outlives any single caller's lifecycle.
  refreshPromise = fetch(`${config.api_url}auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Accept-Language': locale,
      'X-Quotila-App': window.VITE_APP_INSTALLATION_TYPE
    }
  })
    .then(async (refreshResponse) => (refreshResponse.ok ? refreshResponse.json() : false))
    .catch(() => false)
    .finally(() => {
      refreshPromise = null
    })

  return refreshPromise
}

async function parseResponse(response) {
  if (response.status === 204 || response.status === 205) {
    return null
  }

  const contentType = (response.headers.get('content-type') || '').toLowerCase()

  if (contentType.includes('application/json')) return response.json()

  if (isDownloadContent(contentType)) {
    const blob = await response.blob()
    const fileName = parseContentDispositionFileName(response.headers.get('content-disposition'))
    return new File([blob], fileName, { type: blob.type })
  }

  if (contentType.startsWith('text/') || contentType.includes('xml')) return response.text()

  return response.text()
}

function isDownloadContent(contentType) {
  return (
    contentType.includes('application/pdf') ||
    contentType.includes('application/octet-stream') ||
    contentType.startsWith('image/') ||
    contentType.includes('csv')
  )
}

function parseContentDispositionFileName(contentDisposition) {
  if (!contentDisposition) {
    return ''
  }
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition)
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1])
  }
  const asciiMatch = /filename="?([^";]+)"?/i.exec(contentDisposition)
  return asciiMatch?.[1] ?? ''
}

function handleHttpError(error, path) {
  if (error.response) {
    const brandedStore = useBrandedStore()
    const authStore = useAuthStore()
    if (error.response.status === 400) {
      const eventId = captureSentryEvent(error, path)
      badRequestError(error, eventId)
    } else if (error.response.status === 429) {
      tooManyRequestsError(error)
    } else if (error.response.status === 401) {
      const expiry = authStore.sessionExpiry
      const expiryTime = new Date(expiry).getTime()

      // Log 401 if no expiry exists, if expiry is an invalid date or if expiry is in future
      if (!expiry || Number.isNaN(expiryTime) || expiryTime > Date.now()) {
        captureSentryEvent(error, path)
      }
      unauthorisedError(error, authStore, brandedStore)
    } else if (error.response.status === 403) {
      captureSentryEvent(error, path)
      forbiddenError(error, authStore, brandedStore)
    } else {
      const eventId = captureSentryEvent(error, path)
      throw new ServerError(error.message, eventId)
    }
  } else {
    networkError(error)
  }
}

function captureSentryEvent(error, path) {
  let payload = {}
  const response = error?.response
  const rawPayload = response?.config?.data
  const status = response?.status
  if (rawPayload) {
    try {
      payload = JSON.parse(rawPayload)
      delete payload.Token
      delete payload.Password
    } catch {
      payload = { raw: rawPayload }
    }
  }
  return captureEvent({
    message: `Status ${status} : ${path}`,
    fingerprint: ['api-error', status, path],
    tags: {
      status
    },
    extra: {
      payload,
      response: error.response?.data ? JSON.parse(error.response.data) : undefined,
      status
    }
  })
}

function extractResponseToPopUp(response) {
  let message = ''
  if (typeof response === 'string') {
    return { title: DOMPurify.sanitize(response), html: '' }
  }
  const data = Object.fromEntries(Object.entries(response ?? {}).map(([key, value]) => [key.toLowerCase(), value]))
  if (data.title) {
    message += `${data.title}<br>`
  }
  if (data.message) {
    message += `${data.message}<br>`
  }
  if (data.detail) {
    message += `${data.detail}<br>`
  }
  if (data.issues) {
    message += data.issues.join('<br>')
  }
  if (data.errors) {
    message += Object.values(data.errors).join('<br>')
  }

  if (message) {
    return {
      title: DOMPurify.sanitize(message.split('<br>')[0]),
      html: DOMPurify.sanitize(message.split('<br>').slice(1).join('<br>'))
    }
  }

  return { title: 'One or more validation errors has occured' }
}

function badRequestError(error, eventId) {
  let popUp
  if (error.response.data) {
    popUp = extractResponseToPopUp(JSON.parse(error.response.data))
  }
  throw new BadRequestError(error.message, eventId, popUp)
}

function tooManyRequestsError(error) {
  throw new TooManyRequestsError(error.message)
}

function unauthorisedError(error, authStore, brandedStore) {
  window.logoutPush = true
  const redirectURL = authStore.installation === 'branded' ? brandedStore.launchURL : authStore.login_url
  throw new UnauthorisedError(error.message, redirectURL)
}

function forbiddenError(error, authStore, brandedStore) {
  let popUp
  if (error.response.data) {
    popUp = extractResponseToPopUp(JSON.parse(error.response.data))
  }
  const redirectURL = authStore.installation === 'branded' ? brandedStore.launchURL : authStore.login_url
  throw new ForbiddenError(error.message, redirectURL, popUp)
}

function networkError(error) {
  if (error?.retryCount === 2) {
    const eventId = captureEvent({
      message: `Status 0 - ${error?.message}`,
      tags: {
        status: 0
      },
      extra: {
        error,
        retryCount: error.retryCount
      }
    })
    throw new NetworkError(error.message, eventId)
  }
}
